// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '../../Canvas.module.css'
import {
    MousePointer2,
    Hand,
    BoxSelect,
    Undo2,
    Redo2,
    LayoutDashboard,
    Download,
    Bookmark,
    Trash2,
    X,
} from 'lucide-react'
import IconButton from '../../../IconButton/IconButton'
import { Tooltip } from '../../../shared/Tooltip/Tooltip'
import { BRUSH_SIZE_MIN, BRUSH_SIZE_MAX, type ToolbarProps } from './types'
import type { ToolMode } from './useToolMode'

function ToolModeButton({
    mode,
    activeMode,
    icon,
    tooltip,
    shortcut,
    spaceHighlight,
    onChange,
}: {
    mode: ToolMode
    activeMode: ToolMode
    icon: ReactElement
    tooltip: string
    shortcut: string
    spaceHighlight?: boolean
    onChange: (mode: ToolMode) => void
}): ReactElement {
    const isActive = spaceHighlight ? (mode === 'grab') : (activeMode === mode)
    return (
        <Tooltip content={`${tooltip} (${shortcut})`}>
            <IconButton
                className={`${styles.toolbarButton}${isActive ? ` ${styles.toolbarActive}` : ''}`}
                onClick={() => onChange(mode)}
                aria-label={tooltip}
                data-active={isActive}
            >
                {icon}
            </IconButton>
        </Tooltip>
    )
}

export function CanvasToolbar(props: ToolbarProps): ReactElement {
    const {
        brushEnabled,
        toolMode,
        selectedCount,
        readOnly = false,
        // Brush props
        canUndo,
        canRedo,
        brushSizeCssPx,
        onBrushSizeChange,
        onUndo,
        onRedo,
        onExitBrush,
        // Default state
        onToolModeChange,
        onViewportUndo,
        onViewportRedo,
        canViewportUndo,
        canViewportRedo,
        onAutoLayout,
        // Selection state
        onDownload,
        onSave,
        onRemove,
        // Visual
        spacePressed,
    } = props

    const { t } = useTranslation('canvas')

    // Brush state — unchanged from original
    if (brushEnabled) {
        return (
            <div className={styles.toolbar} data-canvas-ui="true">
                {typeof brushSizeCssPx === 'number' && onBrushSizeChange && (
                    <label className={styles.toolbarText}>
                        brush size:
                        <input
                            type="range"
                            min={BRUSH_SIZE_MIN}
                            max={BRUSH_SIZE_MAX}
                            step={1}
                            value={Math.max(BRUSH_SIZE_MIN, Math.min(BRUSH_SIZE_MAX, Math.round(brushSizeCssPx)))}
                            className="slider"
                            onChange={(e) => onBrushSizeChange(Number(e.currentTarget.value))}
                            style={{ marginLeft: 6, verticalAlign: 'middle' }}
                        />
                    </label>
                )}
                <IconButton
                    className={styles.toolbarButton}
                    onClick={onUndo}
                    disabled={!canUndo}
                    aria-label={t('toolbar.undo')}
                >
                    <Undo2 className={styles.icon} aria-hidden="true" />
                </IconButton>
                <IconButton
                    className={styles.toolbarButton}
                    onClick={onRedo}
                    disabled={!canRedo}
                    aria-label={t('toolbar.redo')}
                >
                    <Redo2 className={styles.icon} aria-hidden="true" />
                </IconButton>
                {onExitBrush && (
                    <Tooltip content="Exit brush mode">
                        <IconButton
                            className={styles.toolbarButton}
                            onClick={onExitBrush}
                            aria-label="Exit brush mode"
                        >
                            <X className={styles.icon} aria-hidden="true" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        )
    }

    // Selection state
    if (selectedCount > 0) {
        return (
            <div className={styles.toolbar} data-canvas-ui="true">
                <span className={styles.toolbarSelectedCount}>
                    {t('toolbar.selectedCount', { count: selectedCount })}
                </span>
                <div className={styles.toolbarDivider} />
                <Tooltip content={t('toolbar.download')}>
                    <IconButton
                        className={styles.toolbarButton}
                        onClick={onDownload}
                        disabled={readOnly}
                        aria-label={t('toolbar.download')}
                    >
                        <Download className={styles.icon} aria-hidden="true" />
                    </IconButton>
                </Tooltip>
                <Tooltip content={t('toolbar.save')}>
                    <IconButton
                        className={styles.toolbarButton}
                        onClick={onSave}
                        disabled={readOnly}
                        aria-label={t('toolbar.save')}
                    >
                        <Bookmark className={styles.icon} aria-hidden="true" />
                    </IconButton>
                </Tooltip>
                <Tooltip content={t('toolbar.remove')}>
                    <IconButton
                        className={styles.toolbarButton}
                        onClick={onRemove}
                        disabled={readOnly}
                        aria-label={t('toolbar.remove')}
                    >
                        <Trash2 className={styles.icon} aria-hidden="true" />
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    // Default state — tool modes + canvas actions
    return (
        <div className={styles.toolbar} data-canvas-ui="true">
            <ToolModeButton
                mode="pointer"
                activeMode={toolMode}
                icon={<MousePointer2 className={styles.icon} aria-hidden="true" />}
                tooltip={t('toolbar.pointer')}
                shortcut="V"
                spaceHighlight={spacePressed}
                onChange={onToolModeChange}
            />
            <ToolModeButton
                mode="grab"
                activeMode={toolMode}
                icon={<Hand className={styles.icon} aria-hidden="true" />}
                tooltip={t('toolbar.grab')}
                shortcut="H"
                spaceHighlight={spacePressed}
                onChange={onToolModeChange}
            />
            <ToolModeButton
                mode="select"
                activeMode={toolMode}
                icon={<BoxSelect className={styles.icon} aria-hidden="true" />}
                tooltip={t('toolbar.marqueeSelect')}
                shortcut="S"
                spaceHighlight={spacePressed}
                onChange={onToolModeChange}
            />
            <div className={styles.toolbarDivider} />
            <Tooltip content={t('toolbar.undo')}>
                <IconButton
                    className={styles.toolbarButton}
                    onClick={onViewportUndo}
                    disabled={!canViewportUndo}
                    aria-label={t('toolbar.undo')}
                >
                    <Undo2 className={styles.icon} aria-hidden="true" />
                </IconButton>
            </Tooltip>
            <Tooltip content={t('toolbar.redo')}>
                <IconButton
                    className={styles.toolbarButton}
                    onClick={onViewportRedo}
                    disabled={!canViewportRedo}
                    aria-label={t('toolbar.redo')}
                >
                    <Redo2 className={styles.icon} aria-hidden="true" />
                </IconButton>
            </Tooltip>
            <div className={styles.toolbarDivider} />
            <Tooltip content={t('toolbar.autoLayout')}>
                <IconButton
                    className={styles.toolbarButton}
                    onClick={onAutoLayout}
                    disabled={readOnly}
                    aria-label={t('toolbar.autoLayout')}
                >
                    <LayoutDashboard className={styles.icon} aria-hidden="true" />
                </IconButton>
            </Tooltip>
        </div>
    )
}
